import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api } from "../../api/apiClient";
import type { InventoryBalance, Location } from "../../api/types";
import { isShelfZone } from "../../utils/shelfZones";
import { Modal } from "../Modal";

function parseQty(s: string): number {
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * G-94 S4 — lifted out of `LocationDashboardPage` unchanged, so the inventory
 * page can offer it once that page retires. Same markup, same calls, same
 * rules; only the file it lives in is different.
 */
export function InterLocationTransferModal({
  fromLocation,
  destLocations,
  itemOptionsFromStock,
  onClose,
  onSuccess,
  preset,
}: {
  fromLocation: Location;
  destLocations: Location[];
  itemOptionsFromStock: Map<
    string,
    {
      item: InventoryBalance["item"];
      zones: { zone: InventoryBalance["zone"]; qty: number }[];
    }
  >;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * G-113 S2: a Shopify order's "Transfer now" arrives with the item, the
   * destination and the quantity already known; zones stay a human choice
   * unless the item sits in exactly one shelf zone.
   */
  preset?: { itemId?: string; toLocationId?: string; quantity?: string } | null;
}) {
  const presetItem =
    preset?.itemId && itemOptionsFromStock.has(preset.itemId) ? preset.itemId : "";
  const presetShelfZones = presetItem
    ? (itemOptionsFromStock.get(presetItem)?.zones ?? []).filter(({ zone }) => isShelfZone(zone))
    : [];
  const [itemId, setItemId] = useState(presetItem);
  const [fromZoneId, setFromZoneId] = useState(
    presetShelfZones.length === 1 ? presetShelfZones[0].zone.id : "",
  );
  const [toLocationId, setToLocationId] = useState(
    preset?.toLocationId && destLocations.some((l) => l.id === preset.toLocationId)
      ? preset.toLocationId
      : "",
  );
  const [toZoneId, setToZoneId] = useState("");
  const [quantity, setQuantity] = useState(preset?.quantity ?? "");
  /** The zone resets below are for CHANGES of item / shop; the preset survives the first render. */
  const previousItemId = useRef(presetItem);
  const previousToLocationId = useRef(
    preset?.toLocationId && destLocations.some((l) => l.id === preset.toLocationId)
      ? preset.toLocationId
      : "",
  );
  const [reason, setReason] = useState("Inter-location transfer");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const selectedEntry = itemId ? itemOptionsFromStock.get(itemId) : undefined;
  const maxQty = useMemo(() => {
    if (!selectedEntry || !fromZoneId) return undefined;
    const z = selectedEntry.zones.find((x) => x.zone.id === fromZoneId);
    return z?.qty;
  }, [selectedEntry, fromZoneId]);

  const destLocation = destLocations.find((l) => l.id === toLocationId);

  useEffect(() => {
    if (previousItemId.current !== itemId) {
      previousItemId.current = itemId;
      setFromZoneId("");
    }
  }, [itemId]);

  useEffect(() => {
    if (previousToLocationId.current !== toLocationId) {
      previousToLocationId.current = toLocationId;
      setToZoneId("");
    }
  }, [toLocationId]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    const qty = parseQty(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      setFormError("Quantity must be greater than zero");
      return;
    }
    if (maxQty !== undefined && qty > maxQty) {
      setFormError(`Quantity cannot exceed on-hand (${maxQty}) in the selected zone.`);
      return;
    }
    setSubmitting(true);
    try {
      await api.transferBetweenLocations({
        itemId,
        fromLocationId: fromLocation.id,
        fromZoneId,
        toLocationId,
        toZoneId,
        quantity: qty,
        reason: reason.trim() || "Inter-location transfer",
      });
      onSuccess();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Could not transfer stock");
    } finally {
      setSubmitting(false);
    }
  };

  const itemIds = [...itemOptionsFromStock.keys()];
  const canSubmit =
    itemId &&
    fromZoneId &&
    toLocationId &&
    toZoneId &&
    quantity &&
    destLocations.length > 0;

  return (
    <Modal title="Transfer stock to another location" onClose={onClose}>
      <p style={{ color: "var(--color-text-muted)", marginTop: 0 }}>
        Ship inventory from <strong>{fromLocation.name}</strong> to another shop. Quantities are
        limited to what is on hand in the selected zone.
      </p>
      {destLocations.length === 0 ? (
        <div className="alert error">
          Add another location (Owner) before you can transfer between shops.
        </div>
      ) : (
        <form className="modal-form" onSubmit={handleSubmit}>
          {formError ? <div className="alert error full">{formError}</div> : null}
          <div className="field">
            <label htmlFor="xferItem">Item *</label>
            <select
              id="xferItem"
              required
              value={itemId}
              onChange={(e) => setItemId(e.target.value)}
            >
              <option value="">Select an item with stock here</option>
              {itemIds.map((id) => {
                const e = itemOptionsFromStock.get(id)!;
                return (
                  <option key={id} value={id}>
                    {e.item.name} ({e.item.defaultUom})
                  </option>
                );
              })}
            </select>
          </div>
          <div className="field">
            <label htmlFor="xferFromZone">From zone *</label>
            <select
              id="xferFromZone"
              required
              disabled={!itemId}
              value={fromZoneId}
              onChange={(e) => setFromZoneId(e.target.value)}
            >
              <option value="">Select zone</option>
              {/* G-84: a box is not a source for a cross-shop transfer — it
                  is emptied from its own card, where the person says so. */}
              {selectedEntry?.zones
                .filter(({ zone }) => isShelfZone(zone))
                .map(({ zone, qty }) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name} ({zone.zoneType}) - qty {qty}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="xferDestLoc">Destination location *</label>
            <select
              id="xferDestLoc"
              required
              value={toLocationId}
              onChange={(e) => setToLocationId(e.target.value)}
            >
              <option value="">Select shop</option>
              {destLocations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="xferDestZone">Destination zone *</label>
            <select
              id="xferDestZone"
              required
              disabled={!toLocationId}
              value={toZoneId}
              onChange={(e) => setToZoneId(e.target.value)}
            >
              <option value="">Select zone</option>
              {destLocation?.zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name} ({z.zoneType})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="xferQty">Quantity *</label>
            <input
              id="xferQty"
              type="number"
              min={0.001}
              step="0.001"
              required
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
            {maxQty !== undefined ? (
              <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
                Max available: {maxQty}
              </span>
            ) : null}
          </div>
          <div className="field full">
            <label htmlFor="xferReason">Reason *</label>
            <input
              id="xferReason"
              type="text"
              required
              maxLength={200}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <div className="button-row">
            <button className="button" type="submit" disabled={submitting || !canSubmit}>
              {submitting ? "Transferring..." : "Confirm transfer"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
